import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import { Box, Heading, VideoPlayButton } from 'lucid-components-react';
import React, { useContext } from 'react';
import LazyLoad from 'react-lazyload';

import { mqMax } from 'theme/base';
import LayoutContext from 'contexts/layoutContext';
import { Drupal_ParagraphEnterpriseVideo } from 'generated/graphql';
import { LucidTheme } from 'types/types';
import { Text } from 'theme/typography';

interface EnterpriseVideoProps {
  fields?: Drupal_ParagraphEnterpriseVideo;
}

const EnterpriseVideo = ({ fields }: EnterpriseVideoProps) => {
  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_ct_enterprise_pearson_header
                mktg_ct_enterprise_pearson_large_quote
                mktg_ct_enterprise_pearson_pitch
              }
            }
          }
        }
      }
    `
  );

  const filterFields = (items: Drupal_ParagraphEnterpriseVideo) => {
    return Object.keys(items)
      .filter(item => items[item as keyof Drupal_ParagraphEnterpriseVideo])
      .reduce(
        (res, item) => (
          (res[item] = items[item as keyof Drupal_ParagraphEnterpriseVideo]),
          res
        ),
        {} as {
          [key: string]: string;
        }
      );
  };

  const {
    language: { i18nKey },
    video: { getVideoModal }
  } = useContext(LayoutContext);

  const i18n = i18nFunct(i18nData, i18nKey);
  const defaults = {
    fieldVideoBackgroundImage:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/customers/pearson-quote2-700x360.jpg',
    fieldVideoLink:
      'https://player.vimeo.com/video/98592765?title=0&byline=0&portrait=0&autoplay=1',
    fieldTitle: i18n('mktg-ct-enterprise-pearson-header'),
    fieldQuote: i18n('mktg-ct-enterprise-pearson-large-quote'),
    fieldText: i18n('mktg-ct-enterprise-pearson-pitch')
  };

  let values = fields || defaults;

  if (fields && fields.fieldUseDefaults) {
    const filteredFields = filterFields(fields);
    values = { ...defaults, ...filteredFields };
  }

  return (
    <Box
      as="section"
      css={(theme: LucidTheme) => ({
        background: theme.colors.coolGrey[90],
        display: 'flex',
        flexFlow: 'row nowrap',
        width: '100%',
        [mqMax.mobile]: {
          display: 'block'
        }
      })}
    >
      <LazyLoad offset={200}>
        <Box
          css={{
            backgroundImage: `url(${values.fieldVideoBackgroundImage})`,
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            display: 'flex',
            flex: '0 1 50%',
            position: 'relative',
            [mqMax.mobile]: {
              flex: 'none',
              minHeight: '200px',
              width: '100%'
            }
          }}
        >
          {values.fieldVideoLink && (
            <Box
              css={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
              }}
            >
              <VideoPlayButton
                onClick={() => getVideoModal(values.fieldVideoLink!)}
                size="lg"
              />
            </Box>
          )}
        </Box>
      </LazyLoad>
      <Box
        css={(theme: LucidTheme) => ({
          color: theme.colors.white,
          flex: '0 1 50%',
          padding: `${theme.space[8]}px ${theme.space[9]}px`,
          [mqMax.tabletSmall]: {
            padding: theme.space[7]
          },
          [mqMax.mobile]: {
            flex: 'none',
            padding: theme.space[4],
            width: '100%'
          }
        })}
      >
        {values.fieldTitle && (
          <Heading
            as="h2"
            css={(theme: LucidTheme) => ({
              maxWidth: '385px',
              margin: `0 0 ${theme.space[5]}px`
            })}
            textStyles="headlineXS"
          >
            {values.fieldTitle}

          </Heading>
        )}
        {values.fieldQuote && (
          <Text
            css={(theme: LucidTheme) => ({
              color: theme.colors.white,
              '&::before': {
                content: '"\\0201C"'
              },
              '&::after': {
                content: '"\\0201D"'
              }
            })}
            textStyles="quoteS"
          >
            {values.fieldQuote}
          </Text>
        )}
        {values.fieldText && (
          <Text
            css={(theme: LucidTheme) => ({
              color: theme.colors.white,
              maxWidth: '385px'
            })}
            textStyles="bodyBold"
          >
            {values.fieldText}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default EnterpriseVideo;
